// InputHandler — translates `beforeinput` events into Document
// mutations. The native InputEvent gives us the desired text plus a
// static range describing what should be replaced; we map that range
// to character offsets in the model and dispatch to one of the per-
// inputType handlers below.
//
// Composition (IME) input bypasses this path entirely — see
// editor.js, which holds the `composing` flag and lets the browser
// drive contenteditable until compositionend.

import { Selection } from "./selection.js"

export class InputHandler {
  constructor(document) {
    this.document = document
  }

  handleInput(event, contentElement, blocks) {
    if (this.shouldDeferToCompositor(event)) return
    event.preventDefault()
    const handler = this[`${event.inputType}Handler`]
    if (!handler) {
      console.error("Not handling:", event.inputType)
      return
    }
    const targetRange = event.getTargetRanges()[0]
    if (!targetRange) return
    const { start, end } = Selection.fromDOMRange(targetRange, contentElement, blocks)
    handler.call(this, event, start, end)
  }

  // The browser owns the contenteditable DOM during composition. If we
  // preventDefault here, macOS Option+E (and similar dead keys) loses
  // its visual indicator and the composed character is lost. We sync
  // the model on `compositionend`.
  shouldDeferToCompositor(event) {
    const editor = event.currentTarget
    if (editor && editor.composing) return true
    if (typeof event.inputType === "string" && event.inputType.includes("Composition")) return true
    return false
  }

  insertTextHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertCompositionTextHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertParagraphHandler(_event, start, end) { this.document.insertParagraph(start, end) }
  insertLinkHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertFromYankHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertFromDropHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertFromPasteAsQuotationHandler(event, start, end) {
    const text = event.dataTransfer.getData("text/plain")
    this.document.replaceText(`> ${text}`, start, end)
  }
  insertTransposeHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertReplacementTextHandler(event, start, end) { this.replaceFromInput(event, start, end) }
  insertFromPasteHandler(event, start, end) { this.replaceFromInput(event, start, end) }

  deleteContentBackwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteByCutHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteWordBackwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteWordForwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteSoftLineBackwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteSoftLineForwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteEntireSoftLineHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteHardLineBackwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteHardLineForwardHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteByDragHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteContentHandler(_e, start, end) { this.document.deleteText(start, end) }
  deleteContentForwardHandler(_e, start, end) { this.document.deleteText(start, end) }

  replaceFromInput(event, start, end) {
    this.document.replaceText(extractText(event), start, end)
  }
}

// Extracts the text payload from a beforeinput event.
//
// Order matters here. For paste / drop, dataTransfer is the canonical
// source of truth — Safari (and some Chromium versions) pre-fill
// `event.data` with a stripped, rendered representation that has lost
// markdown markers, so checking `event.data` first silently throws
// away the real clipboard contents. dataTransfer is null for typing
// and IME, so the event.data fallback below still handles those.
//
// Within dataTransfer:
//   - A markdown editor puts the raw markdown in `text/plain` —
//     exactly what we want.
//   - A rendered preview may put a stripped representation in
//     `text/plain` (single line, no markers) but `text/html` still
//     has block elements; HtmlToPlainText recovers paragraph breaks.
// We prefer `text/plain` when it shows evidence of preserved structure
// (newlines OR markdown markers); otherwise we fall back to HTML.
function extractText(event) {
  const fromTransfer = extractFromDataTransfer(event.dataTransfer)
  if (fromTransfer !== null) return fromTransfer
  if (event.data) return event.data
  return ""
}

function extractFromDataTransfer(dt) {
  if (!dt) return null
  const plain = dt.getData("text/plain")
  const html = dt.getData("text/html")
  if (!plain && !html) return null
  // text/html is the rich, consistent source for paste from
  // markdown editors and rendered previews (Typora, Obsidian, browser
  // selections). Their text/plain is unreliable — sometimes stripped,
  // sometimes partially line-broken, sometimes the raw source.
  // htmlToMarkdown reconstructs markers deterministically. Fall back
  // to text/plain only when no HTML is on the clipboard (raw text
  // editors that emit plain text only).
  if (html) return htmlToMarkdown(html)
  return plain || ""
}


// Convert HTML to markdown. Used as the paste fallback when the source
// app's `text/plain` is rendered/stripped (Typora, browser-rendered
// preview pages, etc.). Recovers structure from block elements AND
// re-inserts the markdown markers our editor parses.
//
// This is intentionally narrow — common tags only. Unknown elements
// pass through as their inner content. We don't escape markdown
// special characters in text nodes; the user's intent is to paste
// content "as-is" and over-escaping would corrupt code samples and
// formatted text.
export function htmlToMarkdown(html) {
  const dom = new window.DOMParser().parseFromString(html, "text/html")
  const text = walk(dom.body)
  return text
    .replace(/^\s+|\s+$/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
}

// Extract the text content of a <pre> element while preserving line
// breaks. node.textContent loses them when the source uses <br> or
// nested <div>/<p> rows for each line (Typora's CodeMirror output,
// some browser-copied code blocks, GitHub gist HTML). We walk
// children, treating <br> as "\n" and block elements as line
// boundaries.
function preTextContent(pre) {
  let out = ""
  for (const child of pre.childNodes) out += extractPreNode(child)
  return out
}

function extractPreNode(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || ""
  if (node.nodeType !== Node.ELEMENT_NODE) return ""
  if (node.tagName === "BR") return "\n"
  let inner = ""
  for (const child of node.childNodes) inner += extractPreNode(child)
  if ((node.tagName === "DIV" || node.tagName === "P") && !inner.endsWith("\n")) {
    inner += "\n"
  }
  return inner
}

function walk(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.nodeValue
  if (node.nodeType !== Node.ELEMENT_NODE) return ""
  const inner = () => Array.from(node.childNodes).map(walk).join("")
  switch (node.tagName) {
    case "H1": return `\n\n# ${inner()}\n`
    case "H2": return `\n\n## ${inner()}\n`
    case "H3": return `\n\n### ${inner()}\n`
    case "H4": return `\n\n#### ${inner()}\n`
    case "H5": return `\n\n##### ${inner()}\n`
    case "H6": return `\n\n###### ${inner()}\n`
    case "STRONG":
    case "B":
      return `**${inner()}**`
    case "EM":
    case "I":
      return `*${inner()}*`
    case "S":
    case "STRIKE":
    case "DEL":
      return `~~${inner()}~~`
    case "MARK":
      return `==${inner()}==`
    case "CODE":
      // Inline only — pre handles fenced blocks via its own path.
      return node.parentElement && node.parentElement.tagName === "PRE"
        ? inner()
        : `\`${inner()}\``
    case "PRE":
      return `\n\n\`\`\`\n${preTextContent(node).replace(/\n+$/, "")}\n\`\`\`\n`
    case "A":
      return `[${inner()}](${node.getAttribute("href") || ""})`
    case "IMG":
      return `![${node.getAttribute("alt") || ""}](${node.getAttribute("src") || ""})`
    case "BR":
      return "\n"
    case "HR":
      return "\n\n---\n"
    case "P":
    case "DIV":
    case "SECTION":
    case "ARTICLE":
      return `\n\n${inner()}`
    case "BLOCKQUOTE":
      return "\n\n" + inner()
        .replace(/^\s+|\s+$/g, "")
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n")
    case "UL":
      return "\n\n" + Array.from(node.children)
        .filter((c) => c.tagName === "LI")
        .map((li) => `- ${walk(li).trim()}`)
        .join("\n")
    case "OL": {
      const start = parseInt(node.getAttribute("start") || "1", 10) || 1
      return "\n\n" + Array.from(node.children)
        .filter((c) => c.tagName === "LI")
        .map((li, i) => `${start + i}. ${walk(li).trim()}`)
        .join("\n")
    }
    case "LI":
      return inner()
    case "META":
    case "STYLE":
    case "SCRIPT":
    case "HEAD":
      return ""
    default:
      return inner()
  }
}
